// src/components/Features.jsx - COMPLETELY REDESIGNED
import React, { useState, useEffect, useRef } from 'react';

var isGJ = false;

const Features = () => {
  const domain = window.location.hostname;
  if (domain.includes("gojuris.ai")) {
    isGJ = true;
  }

  const [activeModal, setActiveModal] = useState(null);
  const featuresRef = useRef(null);

  const features = [
    {
      icon: 'bx-brain',
      title: 'Fast and Reliable Search',
      description: 'Smart semantic search powered by advanced AI to find exactly what you need',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#667eea'
    },
    {
      icon: 'bx-book-open',
      title: 'Citation Search',
      description: 'Instantly locate judgments by citations with our intelligent database',
      gradient: 'linear-gradient(135deg, #E91E63 0%, #E91E64 100%)',
      color: '#E91E63'
    },
    {
      icon: 'bx-search-alt',
      title: 'Advanced Filtering',
      description: 'Filter by court, date, judge, and more for precise results',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      color: '#11998e'
    },
    {
      icon: 'bx-line-chart',
      title: 'Case Analytics',
      description: 'Visualize case trends and patterns with powerful analytics',
      gradient: 'linear-gradient(135deg, #c41849 0%, #FF1744 100%)',
      color: '#c41849'
    },
    {
      icon: 'bx-history',
      title: 'Judgment History',
      description: 'Track overruled and superseded judgments automatically',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      color: '#4facfe'
    },
    {
      icon: 'bx-bookmark',
      title: 'Subject Search',
      description: 'Search by subject, filter by keyword—precision at your fingertips',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      color: '#fa709a'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (featuresRef.current) {
      observer.observe(featuresRef.current);
    }

    return () => {
      if (featuresRef.current) {
        observer.unobserve(featuresRef.current);
      }
    };
  }, []);

  return (
    <section className="modern-features-section" ref={featuresRef} id="about">
      <div className="container">
        {/* Section Header */}
        <div className="features-header">
          <h2 className="features-title">
            What's New in <span className="gradient-text">{isGJ ? 'LJSoft Online ' : 'LJSoft Online '}</span>
          </h2>
          <p className="features-subtitle">
            Cutting-edge features designed to revolutionize your legal research experience
          </p>
        </div>

        {/* Features Grid */}
        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className="feature-card-modern"
              style={{
                '--feature-gradient': feature.gradient,
                '--feature-color': feature.color,
                '--card-index': index
              }}
            >
              <div className="feature-icon-wrapper">
                <div className="feature-icon-bg"></div>
                <i className={`bx ${feature.icon} feature-icon`}></i>
              </div>
              
              <div className="feature-content">
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>

              <div className="feature-hover-overlay">
                <i className={`bx ${feature.icon}`}></i>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        /* ============================================
           MODERN FEATURES SECTION
        ============================================ */
        .modern-features-section {
          padding: 80px 0;
          background: #ffffff;
          position: relative;
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.8s ease;
        }

        .modern-features-section.fade-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* Section Header */
        .features-header {
          text-align: center;
          margin-bottom: 56px;
        }

        .features-title {
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 800;
          color: #1a1a1a;
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }

        .features-subtitle {
          font-size: clamp(1rem, 2vw, 1.15rem);
          color: #666;
          max-width: 700px;
          margin: 0 auto;
        }

        /* Features Grid */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 28px;
        }

        /* Feature Card */
        .feature-card-modern {
          position: relative;
          background: white;
          border-radius: 20px;
          padding: 32px 28px;
          border: 2px solid #f0f0f0;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
          cursor: pointer;
          animation: cardFadeIn 0.6s ease calc(var(--card-index) * 0.1s) both;
        }

        @keyframes cardFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .feature-card-modern::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: var(--feature-gradient);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.5s ease;
        }

        .feature-card-modern:hover::before {
          transform: scaleX(1);
        }

        .feature-card-modern:hover {
          border-color: var(--feature-color);
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
        }

        /* Feature Icon */
        .feature-icon-wrapper {
          position: relative;
          width: 80px;
          height: 80px;
          margin-bottom: 24px;
        }

        .feature-icon-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: var(--feature-gradient);
          border-radius: 16px;
          opacity: 0.1;
          transition: all 0.3s ease;
        }

        .feature-card-modern:hover .feature-icon-bg {
          opacity: 0.2;
          transform: scale(1.1) rotate(5deg);
        }

        .feature-icon {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          color: var(--feature-color);
          width: 80px;
          height: 80px;
        }

        /* Feature Content */
        .feature-content {
          position: relative;
          z-index: 1;
        }

        .feature-title {
          font-size: 20px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 12px;
        }

        .feature-description {
          font-size: 15px;
          color: #666;
          line-height: 1.6;
          margin: 0;
        }

        /* Hover Overlay */
        .feature-hover-overlay {
          position: absolute;
          top: 0;
          right: 0;
          width: 120px;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transform: translate(20px, -20px) rotate(15deg);
          transition: all 0.4s ease;
          pointer-events: none;
        }

        .feature-hover-overlay i {
          font-size: 100px;
          color: var(--feature-color);
          opacity: 0.05;
        }

        .feature-card-modern:hover .feature-hover-overlay {
          opacity: 1;
          transform: translate(0, 0) rotate(0deg);
        }

        /* Responsive */
        @media (max-width: 991px) {
          .modern-features-section {
            padding: 60px 0;
          }

          .features-grid {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 20px;
          }

          .feature-card-modern {
            padding: 24px 20px;
          }
        }

        @media (max-width: 767px) {
          .features-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};

export default Features;